import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppLayout from '../../components/AppLayout';
import ImportSummaryDialog from '../../components/ImportSummaryDialog';
import API_BASE_URL from '../../config/api';
import { toast } from 'react-toastify';

export default function Import() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importSummary, setImportSummary] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setSuccess(false);
    } else {
      toast.error('Please select a PDF file');
      event.target.value = '';
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select the backup PDF file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('backupPdf', selectedFile);

    try {
      const token = localStorage.getItem('token');
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 30;
        });
      }, 500);

      const response = await fetch(`${API_BASE_URL}/api/customers/import-backup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        setImportSummary(result);
        setSummaryDialogOpen(true);
        setSuccess(true);
        setSelectedFile(null);
        document.getElementById('pdf-upload-input').value = '';
        toast.success('OkCredit backup imported successfully!');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Backup import failed');
        setSuccess(false);
      }
    } catch (error) {
      console.error('Backup import error:', error);
      toast.error('Failed to import backup. Please try again.');
      setSuccess(false);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    document.getElementById('pdf-upload-input').value = '';
  };

  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: 'calc(100vh - 80px)',
          p: { xs: 2, md: 4 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mb: 4,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              letterSpacing: 3,
              color: '#8B7355',
              textDecoration: 'underline',
              textUnderlineOffset: 12,
              textDecorationThickness: 2,
            }}
          >
            IMPORT BACKUP
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          {/* Upload Section */}
          <Paper
            elevation={8}
            sx={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              mb: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#B39565',
                backgroundColor: '#faf8f6',
              },
            }}
          >
            <input
              id="pdf-upload-input"
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <label htmlFor="pdf-upload-input" style={{ cursor: 'pointer', display: 'block' }}>
              <Box>
                <CloudUploadIcon
                  sx={{
                    fontSize: '4rem',
                    color: '#C4A574',
                    mb: 2,
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    color: '#8B7355',
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  Upload PDF
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#8B7355',
                    opacity: 0.7,
                    mb: 2,
                  }}
                >
                  Click to browse or drag and drop your PDF file
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: '#C4A574',
                    fontWeight: 600,
                  }}
                >
                  Supported format: PDF
                </Typography>
              </Box>
            </label>
          </Paper>

          {/* Selected File Display */}
          {selectedFile && (
            <Box sx={{ mb: 3 }}>
              <Alert
                severity="info"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#fff8ec',
                  color: '#8B7355',
                  border: '1px solid rgba(196,165,116,0.12)',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DescriptionIcon sx={{ mr: 1, color: '#8B7355' }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedFile.name}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={handleRemoveFile}
                  sx={{ color: '#d32f2f', fontWeight: 700 }}
                >
                  Remove
                </Button>
              </Alert>
            </Box>
          )}

          {/* Progress Bar */}
          {uploading && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: '#C4A574', fontWeight: 700, mr: 1 }}>
                  Importing backup...
                </Typography>
                <Typography sx={{ color: '#999' }}>
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e9e7e3',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#C4A574',
                  },
                }}
              />
            </Box>
          )}

          {/* Success Alert */}
          {success && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{
                mb: 3,
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                border: '1px solid #81c784',
                borderRadius: 1,
              }}
            >
              Data imported successfully!
            </Alert>
          )}

          {/* Import Summary Table */}
          {importSummary && (
            <Paper
              elevation={8}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#C4A574' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}>
                        Data Type
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}
                      >
                        Records Imported
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importSummary && (
                      <>
                        <TableRow sx={{ '&:hover': { backgroundColor: '#faf8f6' } }}>
                          <TableCell sx={{ fontWeight: 600 }}>Customers</TableCell>
                          <TableCell align="center" sx={{ color: '#C4A574', fontWeight: 700 }}>
                            {importSummary.customersImported || 0}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                              <Typography sx={{ color: '#4caf50', fontWeight: 700 }}>
                                Success
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:hover': { backgroundColor: '#faf8f6' } }}>
                          <TableCell sx={{ fontWeight: 600 }}>Errors</TableCell>
                          <TableCell align="center" sx={{ color: importSummary.errors?.length > 0 ? '#d32f2f' : '#4caf50', fontWeight: 700 }}>
                            {importSummary.errors?.length || 0}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {importSummary.errors?.length > 0 ? (
                                <>
                                  <ErrorIcon sx={{ color: '#d32f2f', fontSize: '1.2rem' }} />
                                  <Typography sx={{ color: '#d32f2f', fontWeight: 700 }}>
                                    Review
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                                  <Typography sx={{ color: '#4caf50', fontWeight: 700 }}>
                                    None
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderColor: '#C4A574',
                color: '#C4A574',
                fontWeight: 700,
                px: 4,
                py: 1.2,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#f9f7f4',
                  borderColor: '#B39565',
                },
              }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!selectedFile || uploading}
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: '#C4A574',
                color: '#fff',
                fontWeight: 700,
                px: 4,
                py: 1.2,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#B39565',
                },
                '&:disabled': {
                  backgroundColor: '#ddd',
                  color: '#999',
                },
              }}
            >
              {uploading ? 'Importing...' : 'Import'}
            </Button>
          </Box>
        </Box>

        {/* Decorative Fade */}
        <Box
          sx={{
            mt: 6,
            height: 60,
            background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.1))',
            borderRadius: 2,
          }}
        />
      </Box>

      {/* Import Summary Dialog */}
      <ImportSummaryDialog
        open={summaryDialogOpen}
        summary={importSummary}
        onClose={() => setSummaryDialogOpen(false)}
      />
    </AppLayout>
  );
}